import { isPlatformServer } from '@angular/common';
import { Component, EventEmitter, inject, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { CurrentUserService } from '@services/auth/current-user.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { NotificationPushInternalService } from '@services/notifications/notification-push-internal.service';
import { MIN_PASSWORD_LENGTH, ValidationService } from '@services/ui/validation.service';
import { RoleService } from '@services/user/role.service';
import { UserService } from '@services/user/user.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { PhoneComponent } from '@shared/components/phone/phone.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ToggleSwitchComponent } from '@shared/components/toggle-switch/toggle-switch.component';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';
import { catchError, finalize, of } from 'rxjs';

/** Supabase's default minimum password length */

@Component({
	selector: 'app-user-crud',
	imports: [SharedModule, InputComponent, ButtonComponent, PhoneComponent, SelectComponent, ToggleSwitchComponent],
	templateUrl: './user-crud.component.html',
	styleUrl: './user-crud.component.scss',
})
export class UserCrudComponent implements OnInit {
	@Output() userUpdated = new EventEmitter<void>(); // Event to refresh user list

	visible = false;
	isEditing = false;
	currentUserId: number | null = null;
	/** Email the edited user had when the dialog opened: changing it on one's own account needs the current password */
	private originalEmail = '';
	dialogTitle = '';
	actionButtonLabel = '';
	isLoading = false;
	userForm: FormGroup;

	roleOptions: { label: string; value: number }[] = [];

	private platformId = inject(PLATFORM_ID);

	constructor(
		private fb: FormBuilder,
		private userService: UserService,
		private validationService: ValidationService,
		private messageAlertService: MessageAlertService,
		private roleService: RoleService,
		private translocoService: TranslocoService,
		private currentUserService: CurrentUserService,
		private notificationPushInternalService: NotificationPushInternalService,
	) {
		this.userForm = this.fb.group({
			email: ['', [Validators.required, Validators.email]],
			password: ['', this.passwordValidators],
			currentPassword: [''],
			name: ['', [Validators.required]],
			lastName: ['', [Validators.required]],
			lastName2: [''],
			phone: [''],
			roleId: [null, [Validators.required]],
			banned: [false],
		});
	}

	ngOnInit() {
		// The current-password field is required only while it is shown (own account, email changed)
		this.userForm.get('email')?.valueChanges.subscribe(() => this.syncCurrentPasswordValidators());
		if (!isPlatformServer(this.platformId)) {
			this.loadRoles();
		}
	}

	/**
	 * Load available roles for the select
	 */
	private loadRoles(): void {
		this.roleService.getRoleOptions().subscribe({
			next: (roles) => {
				if (roles.length) {
					this.roleOptions = roles.map((role) => ({
						label: role.name,
						value: role.id,
					}));
				}
			},
			error: () => {
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('users.crud.messages.rolesLoadError'),
				);
			},
		});
	}

	/**
	 * True when an admin edits THEIR OWN user and changed the email: the backend then requires
	 * `currentPassword` (400 CURRENT_PASSWORD_REQUIRED / 401 INVALID_CURRENT_PASSWORD).
	 */
	get requiresCurrentPassword(): boolean {
		if (!this.isEditing || this.currentUserId === null) {
			return false;
		}
		if (Number(this.currentUserId) !== this.currentUserService.getUserId()) {
			return false;
		}
		const email = this.userForm.get('email')?.value ?? '';
		return !!email && email !== this.originalEmail;
	}

	private syncCurrentPasswordValidators(): void {
		const control = this.userForm.get('currentPassword');
		if (!control) {
			return;
		}
		if (this.requiresCurrentPassword) {
			control.setValidators([Validators.required]);
		} else {
			control.clearValidators();
		}
		control.updateValueAndValidity({ emitEvent: false });
	}

	/** Password on create: the backend rejects fewer than MIN_PASSWORD_LENGTH characters */
	private readonly passwordValidators = [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)];

	/**
	 * Opens the modal for creating or editing a user.
	 * @param user Optional user data for editing.
	 */
	show(user?: any): void {
		this.visible = true;
		this.isEditing = !!user;
		this.currentUserId = user?.id || null;
		this.originalEmail = user?.email ?? '';
		this.dialogTitle = this.isEditing
			? this.translocoService.translate('users.crud.titleEdit')
			: this.translocoService.translate('users.crud.titleNew');
		this.actionButtonLabel = this.isEditing
			? this.translocoService.translate('common.actions.saveChanges')
			: this.translocoService.translate('users.list.createButton');

		if (this.isEditing && user) {
			// In edit mode, password is optional
			this.userForm.get('password')?.clearValidators();
			this.userForm.get('password')?.setValue('');

			this.userForm.patchValue({
				email: user.email,
				name: user.name,
				lastName: user.lastName,
				lastName2: user.lastName2 || '',
				phone: user.phone || '',
				roleId: user.roleId,
				banned: user.banned ?? false,
			});
		} else {
			// In create mode, password is required
			this.userForm.get('password')?.setValidators(this.passwordValidators);

			this.userForm.reset({
				banned: false,
			});
		}

		this.userForm.get('password')?.updateValueAndValidity();
		this.userForm.get('currentPassword')?.setValue('');
		this.syncCurrentPasswordValidators();
	}

	/**
	 * Handles form submission for creating or updating a user.
	 */
	onSubmit(): void {
		// Prevent double submission
		if (this.isLoading) {
			return;
		}

		if (this.userForm.invalid) {
			this.userForm.markAllAsTouched();

			if (this.userForm.get('roleId')?.errors?.['required']) {
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('users.crud.messages.roleRequired'),
				);
			}
			return;
		}

		this.isLoading = true;
		const formData = this.userForm.value;

		const userData: any = {
			email: formData.email,
			name: formData.name,
			lastName: formData.lastName,
			lastName2: formData.lastName2 || null,
			phone: formData.phone || null,
			roleId: Number(formData.roleId),
			banned: formData.banned ?? false,
		};

		// Only send password in create mode
		if (!this.isEditing) {
			userData.password = formData.password;
		}

		// Own account with a new email: the backend checks the current password
		if (this.requiresCurrentPassword) {
			userData.currentPassword = formData.currentPassword;
		}

		const request$ =
			this.isEditing && this.currentUserId
				? this.userService.updateUser(this.currentUserId.toString(), userData)
				: this.userService.createUser(userData);

		// Keep the dialog open until the server answers: on error the typed values stay
		request$
			.pipe(
				catchError((err) => {
					// errors.<code> from the backend; generic key as fallback
					this.messageAlertService.showMessage(
						'error',
						this.translocoService.translate('common.labels.error'),
						translateApiError(err, this.translocoService, 'users.crud.messages.saveError'),
					);

					return of(null);
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((response: any) => {
				if (response) {
					this.visible = false;
					this.userUpdated.emit(); // refresh the list only when something was saved
					if (!this.isEditing) {
						// Send push notification to the creator
						this.sendNewUserPushNotification(userData.email);
					}
					const message = this.isEditing
						? this.translocoService.translate('users.crud.messages.updateSuccess')
						: this.translocoService.translate('users.crud.messages.createSuccess');
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						message,
					);
				}
			});
	}

	/**
	 * Sends a push notification to the creator when a new user is created
	 */
	private sendNewUserPushNotification(userEmail: string): void {
		const currentUser = this.currentUserService.getCurrentUser();
		if (!currentUser?.userId) {
			return;
		}

		const notificationData = {
			title: this.translocoService.translate('users.crud.messages.newUserNotificationTitle'),
			description: `Email: ${userEmail}`,
		};

		this.notificationPushInternalService
			.createNotificationPush(notificationData)
			.pipe(
				catchError(() => {
					return of(null);
				}),
			)
			.subscribe();
	}

	/**
	 * Finishes the save process with success message
	 */
	/**
	 * Retrieves validation error messages for a given form field.
	 */
	getErrorMessage(field: string): string | null {
		const control = this.userForm?.get(field);
		if (control?.errors) {
			const errorKey = Object.keys(control.errors)[0];
			// With the validator's params: {{requiredLength}} for minlength
			return this.validationService.getValidatorErrorMessage(errorKey, control.errors[errorKey]) || null;
		}
		return null;
	}

	/**
	 * Handles "Enter" key press to submit the form.
	 */
	submitOnEnter(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.onSubmit();
		}
	}

	/**
	 * Safe wrapper for submitOnEnter that handles Event type
	 */
	submitOnEnterSafe(event: Event): void {
		this.submitOnEnter(event as KeyboardEvent);
	}
}
